/**
 * Runs analytics work just after the next paint instead of inside the event handler.
 *
 * INP is measured from the tap to the next paint, so anything the handler does
 * synchronously - GTM/GA pushes, payload building - is counted. Moving that work one
 * frame out takes it off the measured path without changing what is sent.
 *
 * requestIdleCallback is deliberately not used here. Idle callbacks run "whenever the
 * browser is free", which on a busy page can be much later than the timeout suggests, and
 * a queued callback is simply lost if the user navigates first. That is an unacceptable
 * trade for revenue-attributed events. rAF -> setTimeout(0) lands ~one frame later and is
 * guaranteed to run, and the queue is flushed on pagehide/visibilitychange so nothing is
 * dropped when the page goes away.
 */
type Task = () => void;

const queue: Task[] = [];
let scheduled = false;
let listenersBound = false;

const runQueue = (): void => {
  scheduled = false;
  // splice so a task that schedules more work does not re-enter this drain
  const tasks = queue.splice(0, queue.length);
  tasks.forEach(task => {
    try {
      task();
    } catch {
      // analytics must never break the interaction that scheduled it
    }
  });
};

const bindFlushListeners = (): void => {
  if (listenersBound) {
    return;
  }
  listenersBound = true;
  window.addEventListener('pagehide', runQueue);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      runQueue();
    }
  });
};

const schedule = (): void => {
  if (scheduled) {
    return;
  }
  scheduled = true;
  // rAF fires before the next paint; the nested timeout lands just after it, so the
  // interaction is measured without this work but the task still runs about a frame later.
  requestAnimationFrame(() => {
    setTimeout(runQueue, 0);
  });
};

const deferAnalytics = (fn: Task): void => {
  if (typeof window === 'undefined' || typeof requestAnimationFrame === 'undefined') {
    fn();
    return;
  }
  bindFlushListeners();
  queue.push(fn);
  schedule();
};

/**
 * Same after-paint timing for UI work (opening/closing a drawer or modal, applying a filter).
 * Unlike deferAnalytics there is no queue and no try/catch: errors must surface, and there is
 * nothing to flush on pagehide.
 */
export const runAfterPaint = (fn: Task): void => {
  if (typeof window === 'undefined' || typeof requestAnimationFrame === 'undefined') {
    fn();
    return;
  }
  requestAnimationFrame(() => {
    setTimeout(fn, 0);
  });
};

/** Wraps an event handler so its body runs after the next paint, with the same arguments. */
export const afterPaint = <A extends unknown[]>(handler: (...args: A) => void) => (
  ...args: A
): void => {
  runAfterPaint(() => handler(...args));
};

export default deferAnalytics;
